(function () {
  // A paginated page of search results can render far fewer cards than it holds
  // results (non-product results and `unsellable` products are skipped), so one
  // click may have to walk several pages before it adds anything.
  const MAX_CHAINED_FETCHES = 25;
  const MAX_RESTORE_FETCHES = 60;

  let currentLoad = null;

  // ============================================================
  // INFINITE SCROLL - Configuration
  // Automatically loads the next products when the user approaches
  // the bottom of the product grid.
  // Preloading starts 1500px before the Load More button is visible.
  // ============================================================
  const INFINITE_SCROLL_MARGIN = '1500px 0px';

  let infiniteScrollObserver = null;
  let isRestoringProducts = false;

  function getGridList(root) {
    const grid = (root || document).getElementById('product-grid');
    if (!grid) return null;
    // Collection template: #product-grid is the <ul>. Search template: it wraps one.
    return grid.tagName === 'UL' ? grid : grid.querySelector('ul');
  }

  function getCards(list) {
    if (!list) return [];
    return Array.from(list.children).filter((el) => el.tagName === 'LI');
  }

  // Excludes the promo tile, which is an <li> but not a product.
  function countProducts(list) {
    return getCards(list).filter((card) => card.hasAttribute('data-product-id')).length;
  }

  function cardKey(card) {
    return card.getAttribute('data-product-id') || '';
  }

  function saveScrollState() {
    const list = getGridList();
    if (!list) return;

    sessionStorage.setItem('loadedProducts', countProducts(list));
    sessionStorage.setItem('scrollPosition', window.scrollY);
  }

  function bindProductLinks() {
    document.querySelectorAll('.link-redirect-to-product').forEach((link) => {
      if (link.dataset.listenerAttached) return;
      link.dataset.listenerAttached = true;
      link.addEventListener('click', saveScrollState);
    });
  }

  function removeLoadMore(loadMoreBtn) {
    const wrapper = loadMoreBtn.closest('.load-more-wrapper');
    (wrapper || loadMoreBtn).remove();
  }

  function attachLoadMoreHandler() {
    const loadMoreBtn = document.getElementById('load-more');

    if (!loadMoreBtn || !getGridList()) return;

    // Prevent duplicate event listener
    if (loadMoreBtn.hasAttribute('data-handler-attached')) return;
    loadMoreBtn.setAttribute('data-handler-attached', 'true');

    loadMoreBtn.addEventListener('click', function () {
      triggerLoadMore(loadMoreBtn);
    });
  }

  // ============================================================
  // INFINITE SCROLL - Observer initialization
  //
  // Uses the existing Load More button as a trigger.
  // The button remains in the DOM but is visually hidden.
  //
  // The observer is disconnected during loading and reconnected
  // afterward to prevent the scroll from getting stuck.
  //
  // It is also recreated after filtering or sorting.
  // ============================================================
  function initInfiniteScroll() {
    if (infiniteScrollObserver) {
      infiniteScrollObserver.disconnect();
      infiniteScrollObserver = null;
    }

    const button = document.getElementById('load-more');

    if (!button || !getGridList()) return;

    // INFINITE SCROLL - Hide the button without removing it.
    const wrapper = button.closest('.load-more-wrapper');

    if (wrapper) {
      wrapper.style.visibility = 'hidden';
    } else {
      button.style.visibility = 'hidden';
    }

    infiniteScrollObserver = new IntersectionObserver(
      async (entries) => {
        if (!entries[0].isIntersecting || isRestoringProducts) return;

        const loadMoreBtn = document.getElementById('load-more');

        if (
          !loadMoreBtn ||
          loadMoreBtn.disabled ||
          !loadMoreBtn.getAttribute('data-next-url')
        ) {
          return;
        }

        // INFINITE SCROLL - Pause observation during loading.
        const observer = infiniteScrollObserver;
        observer.unobserve(loadMoreBtn);

        // INFINITE SCROLL - Reuse the existing pagination function.
        await triggerLoadMore(loadMoreBtn);

        // INFINITE SCROLL - Resume observation after loading.
        // This prevents loading from stopping when scrolling quickly.
        if (
          observer === infiniteScrollObserver &&
          document.contains(loadMoreBtn) &&
          loadMoreBtn.getAttribute('data-next-url')
        ) {
          observer.observe(loadMoreBtn);
        }
      },
      {
        rootMargin: INFINITE_SCROLL_MARGIN,
        threshold: 0,
      }
    );

    infiniteScrollObserver.observe(button);
  }

  function triggerLoadMore(loadMoreBtn) {
    if (loadMoreBtn.disabled) return currentLoad || Promise.resolve();

    loadMoreBtn.disabled = true;
    loadMoreBtn.setAttribute('aria-busy', 'true');
    currentLoad = loadNextPage(loadMoreBtn, MAX_CHAINED_FETCHES);
    return currentLoad;
  }

  function loadNextPage(loadMoreBtn, attemptsLeft) {
    const list = getGridList();
    const nextUrl = loadMoreBtn.getAttribute('data-next-url');

    if (!list || !nextUrl) {
      removeLoadMore(loadMoreBtn);
      return Promise.resolve();
    }

    return fetch(nextUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status} for ${nextUrl}`);
        return res.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newCards = getCards(getGridList(doc));

        const seen = new Set(getCards(list).map(cardKey).filter(Boolean));
        let added = 0;

        newCards.forEach((card) => {
          const key = cardKey(card);
          if (key && seen.has(key)) return;
          if (key) seen.add(key);

          const clone = document.importNode(card, true);

          // The first card of every rendered page carries the theme's card <script>/<link>
          // tags (they sit behind `skip_styles`). Those assets are already on the page, so
          // drop the scripts rather than re-running them once per appended page.
          clone.querySelectorAll('script').forEach((script) => script.remove());

          const appended = list.appendChild(clone);
          if (typeof initSwatches === 'function') initSwatches(appended);
          added += 1;
        });

        bindProductLinks();

        const nextBtn = doc.querySelector('#load-more[data-next-url]');
        const followingUrl = nextBtn && nextBtn.getAttribute('data-next-url');

        if (followingUrl) {
          loadMoreBtn.setAttribute('data-next-url', followingUrl);
        } else {
          loadMoreBtn.removeAttribute('data-next-url');
        }

        updateProgress();

        // Nothing new on that page — keep walking so a click always yields products.
        if (added === 0 && followingUrl && attemptsLeft > 1) {
          return loadNextPage(loadMoreBtn, attemptsLeft - 1);
        }

        if (followingUrl) {
          loadMoreBtn.disabled = false;
          loadMoreBtn.removeAttribute('aria-busy');
        } else {
          removeLoadMore(loadMoreBtn);
        }
      })
      .catch((err) => {
        console.error('[Load more failed]', err);
        removeLoadMore(loadMoreBtn);
      });
  }

  function updateProgress() {
    const list = getGridList();
    const wrapper = document.getElementById('product-progress-wrapper');
    const totalEl = document.getElementById('products-total');
    const shownEl = document.getElementById('products-shown');
    const fillEl = document.getElementById('progress-fill');

    if (!list || !wrapper || !totalEl || !shownEl || !fillEl) return;

    const productsShown = countProducts(list);
    const productsTotal = parseInt(wrapper.getAttribute('data-products-total'), 10) || 0;

    shownEl.textContent = productsShown;

    const progress = productsTotal > 0 ? Math.min((productsShown / productsTotal) * 100, 100) : 0;
    fillEl.style.width = `${progress}%`;
  }

  async function loadUntil(targetCount) {
    let fetches = 0;

    while (fetches < MAX_RESTORE_FETCHES) {
      const list = getGridList();
      if (!list || countProducts(list) >= targetCount) break;

      const loadMoreBtn = document.getElementById('load-more');
      if (!loadMoreBtn || !loadMoreBtn.getAttribute('data-next-url')) break;

      fetches += 1;
      await triggerLoadMore(loadMoreBtn);
    }
  }

  function restoreScrollAndProducts() {
    const loadedProducts = parseInt(sessionStorage.getItem('loadedProducts') || 0, 10);
    const scrollPosition = parseInt(sessionStorage.getItem('scrollPosition') || 0, 10);

    sessionStorage.removeItem('loadedProducts');
    sessionStorage.removeItem('scrollPosition');

    const navEntry = performance.getEntriesByType('navigation')[0];
    const isBackNavigation = navEntry && navEntry.type === 'back_forward';

    if (!loadedProducts || !isBackNavigation) return;

    // ============================================================
    // INFINITE SCROLL - Scroll restoration protection
    // Prevent automatic loading while previously viewed products
    // are being restored after returning from a product page.
    // ============================================================
    isRestoringProducts = true;

    loadUntil(loadedProducts)
      .then(() => {
        window.scrollTo({
          top: scrollPosition,
          behavior: 'smooth',
        });
      })
      .finally(() => {
        // INFINITE SCROLL - Resume automatic loading after restoration.
        isRestoringProducts = false;

        // Reinitialize the observer to check the restored position.
        initInfiniteScroll();
      });
  }

  document.addEventListener('DOMContentLoaded', () => {
    attachLoadMoreHandler();
    bindProductLinks();
    restoreScrollAndProducts();

    // INFINITE SCROLL - Activate automatic loading on initial page load.
    initInfiniteScroll();
  });

  document.addEventListener('facet-filter-updated', () => {
    requestAnimationFrame(() =>
      setTimeout(() => {
        attachLoadMoreHandler();
        bindProductLinks();

        // INFINITE SCROLL - Reinitialize after filters or sorting.
        initInfiniteScroll();
      }, 500)
    );
  });
})();